import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import { CreatePaymentMethodRootParamList } from "../../../../types";
import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import { CreatePaymentMethodScreen } from "../CreatePaymentMethodScreen";
import { PaymentMethodFormProvider } from "../../components/PaymentMethodForm/PaymentMethodFormProvider";
import { SelectPaymentTypeScreen } from "../../components/SelectPaymentTypeScreen";
import { useCreateEditPaymentMethodContextProvider } from "./hooks";
import {
  CreateEditPaymentMethodContext,
  OnAfterAddPaymentMethodTerminalFn,
} from "./context";
import React from "react";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import { PaymentMethodFormValues } from "../../components/PaymentMethodForm/types";
import { SelectRetailerPayTypeModalScreen } from "../../../../components/SelectRetailerPayTypeModalScreen";
import { SelectCashRoundingScreen } from "../../components/SelectCashRoundingScreen";
import { SelectRoundingAlgorithmScreen } from "../../components/SelectRoundingAlgorithmScreen/SelectRoundingAlgorithmScreen";

export type CreatePaymentMethodRootScreenProps = StackScreenProps<
  {
    CreatePaymentMethodRootScreen: {
      terminalIdToAdd?: string;
      defaultFormData?: Partial<PaymentMethodFormValues>;
    };
  },
  "CreatePaymentMethodRootScreen"
>;

export const CreatePaymentMethodRootScreen = ({
  navigation,
  route,
}: CreatePaymentMethodRootScreenProps) => {
  const maybeParentContext = useCreateEditPaymentMethodContextProvider();
  const { fetchAndSyncEntity } = useSetupAllStartupEntities();
  const Stack = createStackHeaderNavigator<CreatePaymentMethodRootParamList>();

  const handleOnAfterAddPaymentMethodTerminal: OnAfterAddPaymentMethodTerminalFn =
    React.useCallback(async (...props) => {
      if (maybeParentContext?.onAfterAddPaymentMethodTerminal) {
        await maybeParentContext.onAfterAddPaymentMethodTerminal(...props);
      }
      await fetchAndSyncEntity("payment_methods");
      await fetchAndSyncEntity("pay_types");
      await fetchAndSyncEntity("terminals");
      navigation.pop();
    }, []);

  const handleOnAfterDeletePaymentMethod = React.useCallback(async () => {
    if (maybeParentContext?.onAfterDeletePaymentMethod) {
      await maybeParentContext.onAfterDeletePaymentMethod(navigation);
    }
    await fetchAndSyncEntity("payment_methods");
    await fetchAndSyncEntity("pay_types");
    await fetchAndSyncEntity("terminals");

    navigation.pop();
  }, []);

  return (
    <CreateEditPaymentMethodContext.Provider
      value={{
        onAfterSavePaymentMethod: maybeParentContext?.onAfterSavePaymentMethod,
        onAfterAddPaymentMethodTerminal: handleOnAfterAddPaymentMethodTerminal,
        onAfterDeletePaymentMethod: handleOnAfterDeletePaymentMethod,
      }}
    >
      <PaymentMethodFormProvider
        defaultFormValues={{
          terminalIdToAdd: route.params?.terminalIdToAdd,
          ...route.params?.defaultFormData,
        }}
      >
        <Stack.Navigator
          screenOptions={{
            animationEnabled: true,
          }}
        >
          <Stack.Group
            screenOptions={{
              headerShown: true,
              headerLeft: () => <ModalCloseButton type="Cancel" />,
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
              title: "",
            }}
          >
            <Stack.Screen
              name="CreatePaymentMethodScreen"
              component={CreatePaymentMethodScreen}
            />
            <Stack.Screen
              name="SelectPaymentTypeScreen"
              component={SelectPaymentTypeScreen}
            />
            <Stack.Screen
              name="SelectRetailerPayTypeModalScreen"
              component={SelectRetailerPayTypeModalScreen}
            />
            <Stack.Screen
              name="SelectCashRoundingScreen"
              component={SelectCashRoundingScreen}
            />
            <Stack.Screen
              name="SelectRoundingAlgorithmScreen"
              component={SelectRoundingAlgorithmScreen}
            />
          </Stack.Group>
        </Stack.Navigator>
      </PaymentMethodFormProvider>
    </CreateEditPaymentMethodContext.Provider>
  );
};
